'use client';

import * as React from "react";
import type { PeekPlayerState } from "./PeekPlayer";
import type { MiniIDEState } from "../MiniIDE/MiniIDE";

/**
 * Interaction-matrix H2 enforcement helper.
 *
 * MV5 Peek + Mini-IDE spec: only one overlay may be open at a time. If a
 * caller opens PeekPlayer to `compact/expanded/full`, the MiniIDE (if
 * currently non-chip) collapses to `chip`, and vice-versa.
 *
 * The `empty` state on either side represents "not available for this
 * surface" and does not participate in the mutual-exclusion rule (an empty
 * chip stays empty regardless of the sibling).
 */

export interface OverlayPairState {
  peek: PeekPlayerState;
  mini: MiniIDEState;
}

export interface OverlayPairApi {
  peek: PeekPlayerState;
  mini: MiniIDEState;
  setPeek: (next: PeekPlayerState) => void;
  setMini: (next: MiniIDEState) => void;
  closeAll: () => void;
}

const OVERLAY_STATES = new Set(["compact", "expanded", "full"]);

function isOverlay(s: string): boolean {
  return OVERLAY_STATES.has(s);
}

export function useOverlayPair(initial?: Partial<OverlayPairState>): OverlayPairApi {
  const [peek, setPeekRaw] = React.useState<PeekPlayerState>(initial?.peek ?? "chip");
  const [mini, setMiniRaw] = React.useState<MiniIDEState>(initial?.mini ?? "chip");

  const setPeek = React.useCallback((next: PeekPlayerState) => {
    setPeekRaw((prev) => {
      // If we're opening peek to an overlay state and mini is currently on an
      // overlay, fold mini to chip. Uses a functional state read on mini to
      // stay correct under batched updates.
      if (isOverlay(next) && prev !== next) {
        setMiniRaw((m) => (isOverlay(m) ? "chip" : m));
      }
      return next;
    });
  }, []);

  const setMini = React.useCallback((next: MiniIDEState) => {
    setMiniRaw((prev) => {
      if (isOverlay(next) && prev !== next) {
        setPeekRaw((p) => (isOverlay(p) ? "chip" : p));
      }
      return next;
    });
  }, []);

  const closeAll = React.useCallback(() => {
    setPeekRaw((p) => (isOverlay(p) ? "chip" : p));
    setMiniRaw((m) => (isOverlay(m) ? "chip" : m));
  }, []);

  return { peek, mini, setPeek, setMini, closeAll };
}
